import { useCallback, useMemo, useRef } from 'react';
import { PartnerSetting, usePartnerSettings } from 'src/graphql';
import {
  PreferencesChange,
  SettingsPreferencesFormFields,
  SlaTaxExemptionFormModel,
} from 'src/pages/Settings/preferences/types';
import { parseSlaTaxExemption } from 'src/pages/Settings/preferences/utils';
import { useGlobalStore } from 'src/store';

interface UseSlaTaxExemptionParams {
  getValues: () => SlaTaxExemptionFormModel;
  setValue: (value: SlaTaxExemptionFormModel) => void;
  handleChange: (data: PreferencesChange) => void | Promise<void>;
}

export function useSlaTaxExemption({
  getValues,
  setValue,
  handleChange,
}: UseSlaTaxExemptionParams) {
  const partnerId = useGlobalStore.use.selectedPartnerId();

  const { data: slaTaxExemptionRecords } = usePartnerSettings({
    partnerId,
    partnerSetting: PartnerSetting.SlaTaxExemption,
  });

  const setting = Array.isArray(slaTaxExemptionRecords)
    ? slaTaxExemptionRecords.find((r) => r.partnerId === partnerId)
    : undefined;

  const serverValue = useMemo(
    () => parseSlaTaxExemption(setting?.value),
    [setting?.value],
  );

  const dirtyRef = useRef(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const save = useCallback(
    (config: SlaTaxExemptionFormModel) => {
      const result = handleChange({
        rule: SettingsPreferencesFormFields.SlaTaxExemption,
        value: JSON.stringify(config),
        id: setting?.partnerSettingValueId ?? null,
        partnerId: setting?.partnerId ?? null,
      });
      void Promise.resolve(result).then(() => {
        dirtyRef.current = false;
      });
    },
    [handleChange, setting],
  );

  const handleExemptToggle = useCallback(
    (checked: boolean) => {
      dirtyRef.current = true;
      const updated = { ...getValues(), exempt: checked };
      setValue(updated);
      save(updated);
    },
    [getValues, setValue, save],
  );

  const handleCustomerIdChange = useCallback(
    (value: string) => {
      dirtyRef.current = true;
      const updated = { ...getValues(), exemptaxCustomerId: value };
      setValue(updated);
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => save(updated), 500);
    },
    [getValues, setValue, save],
  );

  const shouldSyncFromServer = !dirtyRef.current;

  return {
    serverValue,
    shouldSyncFromServer,
    handleExemptToggle,
    handleCustomerIdChange,
  };
}
